import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API_URL } from "@/config";
import { Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const PAYMENT_MODES = [
    { value: "INVOICE_ON_CLOSE", label: "Invoice When Closed" },
    { value: "DEPOSIT",          label: "Deposit Upfront" },
    { value: "NONE",             label: "No Charge" },
];

export default function ServiceCreateROPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [form, setForm] = useState({
        vehicle_vin:  searchParams.get("vin")        ?? "",
        vehicle_desc: searchParams.get("desc")       ?? "",
        ro_notes:     "",
        payment_mode: "INVOICE_ON_CLOSE",
    });
    const apptId     = searchParams.get("apptId")     ? Number(searchParams.get("apptId"))     : undefined;
    const customerId = searchParams.get("customerId")  ? Number(searchParams.get("customerId")) : undefined;

    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const res = await axios.post(
                `${API_URL}/repair-shops/staff/repair-orders`,
                {
                    ...form,
                    appt_id:     apptId,
                    customer_id: customerId,
                    vehicle_vin:  form.vehicle_vin  || undefined,
                    vehicle_desc: form.vehicle_desc || undefined,
                },
                { withCredentials: true }
            );
            if (res.data.ok) {
                navigate(`/service/repair-orders/${res.data.repair_order.ro_id}`);
            } else {
                setError(res.data.message ?? "Failed to create RO");
            }
        } catch (e: any) {
            setError(e.response?.data?.message ?? "Server error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-xl mx-auto space-y-6">
            <div>
                <h1 className="text-2xl font-light uppercase tracking-wide">New Repair Order</h1>
                {apptId && <p className="text-muted-foreground text-sm mt-1">From Appointment #{apptId}</p>}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                <Card className="p-5 space-y-4">
                    <div>
                        <label className="text-sm font-medium">VIN</label>
                        <Input
                            value={form.vehicle_vin}
                            onChange={(e) => setForm({ ...form, vehicle_vin: e.target.value })}
                            placeholder="Vehicle identification number"
                            className="mt-1 font-mono"
                            maxLength={17}
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium">Vehicle Description</label>
                        <Input
                            value={form.vehicle_desc}
                            onChange={(e) => setForm({ ...form, vehicle_desc: e.target.value })}
                            placeholder="e.g. 2022 Toyota Camry XSE"
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium">Notes</label>
                        <textarea
                            value={form.ro_notes}
                            onChange={(e) => setForm({ ...form, ro_notes: e.target.value })}
                            rows={3}
                            className="mt-1 block w-full rounded-lg border bg-background px-3 py-2 text-sm resize-none"
                            placeholder="Initial complaint, customer notes..."
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium">Payment Mode</label>
                        <select
                            value={form.payment_mode}
                            onChange={(e) => setForm({ ...form, payment_mode: e.target.value })}
                            className="mt-1 block w-full rounded-lg border bg-background px-3 py-2 text-sm"
                        >
                            {PAYMENT_MODES.map((m) => (
                                <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                        </select>
                    </div>
                </Card>

                {error && <p className="text-red-500 text-sm">{error}</p>}

                <div className="flex gap-3">
                    <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
                    <Button type="submit" className="bg-indigo-600 text-white hover:bg-indigo-700" disabled={loading}>
                        {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                        Create Repair Order
                    </Button>
                </div>
            </form>
        </div>
    );
}
